import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Tag,
  Button,
  Space,
  Select,
  Typography,
  Divider,
  Tooltip,
} from "antd";
import {
  ReloadOutlined,
  UserAddOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import api from "../../../shared/api.jsx";

const { Title, Text } = Typography;

const statusColor = (status) => {
  if (status === "queued") return "blue";
  if (status === "assigned") return "gold";
  if (status === "resolved") return "green";
  return "default";
};

const priorityColor = (priority) => {
  if (priority === "high") return "red";
  if (priority === "normal") return "geekblue";
  if (priority === "low") return "cyan";
  return "default";
};

const QueuePanel = ({
  onTicketClaimed,
  onTicketReleased,
  agentId = "agent_1",
}) => {
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({
    queued: 0,
    assigned: 0,
    resolved: 0,
    total: 0,
  });
  const [statusFilter, setStatusFilter] = useState("queued");
  const [priorityFilter, setPriorityFilter] = useState(undefined);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter, limit: 25 };
      if (priorityFilter) params.priority = priorityFilter;
      const res = await api.get("/agent/queue", { params });
      const data = res.data || res;
      setTickets(data.data?.tickets || []);
      setStats(
        data.data?.stats || { queued: 0, assigned: 0, resolved: 0, total: 0 }
      );
    } catch (e) {
      // swallow; page-level error boundary can show toast
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter]);

  const filteredTickets = useMemo(() => tickets, [tickets]);

  const claim = async (ticketId) => {
    setLoading(true);
    try {
      await api.post(`/agent/queue/${ticketId}/claim`, { agentId });
      onTicketClaimed?.(ticketId);
      fetchQueue();
    } catch (e) {
      setLoading(false);
    }
  };

  const release = async (ticketId, resumeBot = false) => {
    setLoading(true);
    try {
      await api.post(`/agent/queue/${ticketId}/release`, {
        agentId,
        resumeBot,
      });
      onTicketReleased?.(ticketId);
      fetchQueue();
    } catch (e) {
      setLoading(false);
    }
  };

  return (
    <Card
      title={
        <Space align="center">
          <Title level={4} style={{ margin: 0 }}>
            Queue
          </Title>
          <Tag color="blue">Queued: {stats.queued}</Tag>
          <Tag color="gold">Assigned: {stats.assigned}</Tag>
          <Tag color="green">Resolved: {stats.resolved}</Tag>
          <Tooltip title="Refresh">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchQueue}
              loading={loading}
            />
          </Tooltip>
        </Space>
      }
      variant="outlined"
      loading={loading}
    >
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 160 }}
          options={[
            { value: "queued", label: "Queued" },
            { value: "assigned", label: "Assigned" },
            { value: "resolved", label: "Resolved" },
          ]}
        />
        <Select
          allowClear
          placeholder="Priority"
          value={priorityFilter}
          onChange={setPriorityFilter}
          style={{ width: 160 }}
          options={[
            { value: "high", label: "High" },
            { value: "normal", label: "Normal" },
            { value: "low", label: "Low" },
          ]}
        />
      </Space>

      {filteredTickets.map((t) => (
        <Card key={t.id} size="small" style={{ marginBottom: 8 }}>
          <Space
            align="start"
            style={{ width: "100%", justifyContent: "space-between" }}
          >
            <Space direction="vertical" size={2}>
              <Space wrap>
                <Tag color={statusColor(t.status)}>{t.status}</Tag>
                <Tag color={priorityColor(t.priority)}>{t.priority}</Tag>
                {t.queuePosition != null && <Tag>{`#${t.queuePosition}`}</Tag>}
              </Space>
              <Text type="secondary">waId: {t.waId}</Text>
              <Text type="secondary">phone: {t.customerPhone}</Text>
            </Space>
            <Space>
              {t.status === "queued" && (
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => claim(t.id)}
                >
                  Claim
                </Button>
              )}
              {t.status === "assigned" && (
                <Space>
                  <Button onClick={() => release(t.id, false)}>Release</Button>
                  <Button
                    type="dashed"
                    icon={<ExportOutlined />}
                    onClick={() => release(t.id, true)}
                  >
                    Resolve & Resume Bot
                  </Button>
                </Space>
              )}
            </Space>
          </Space>
        </Card>
      ))}

      {filteredTickets.length === 0 && (
        <>
          <Divider />
          <Text type="secondary">
            No tickets found for the selected filters.
          </Text>
        </>
      )}
    </Card>
  );
};

export default QueuePanel;
